import { Stack } from '@mui/material';
import {
  Editor,
  FilePickerNode,
  IMAGE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES_PER_MESSAGE,
  MAX_TOTAL_EMAIL_MESSAGE_SIZE_BYTES,
  RichTextEditorFile,
  ToolbarButton,
  validateFiles,
  WritableRef,
} from '@provision/rich-text-editor';
import { ChangeEvent, useCallback, useState } from 'react';
import { EditorToolbarButtons } from 'src/components/RichTextEditor/editor-toolbar-buttons';
import { useFileUpload } from 'src/graphql/entities/files-upload';
import { useNotifications } from 'src/utils/hooks';

export const ToolbarMenu = ({
  toolbarButtons,
  editor,
  fileInputRef,
}: {
  toolbarButtons: ToolbarButton[];
  fileInputRef: WritableRef<HTMLInputElement | null>;
  editor: Editor | null;
}) => {
  const [files, setFiles] = useState<RichTextEditorFile[]>([]);

  const { showErrorAlert } = useNotifications();

  const { uploadFile } = useFileUpload();

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { target } = event;

      if (!target?.files) {
        return;
      }

      const files = Array.from(target.files);
      const validationResult = validateFiles(files, {
        maxFiles: MAX_FILES_PER_MESSAGE,
        maxFileSize: MAX_FILE_SIZE_BYTES,
        supportedFileTypes: IMAGE_TYPES,
        maxTotalFilesSizeBytes: MAX_TOTAL_EMAIL_MESSAGE_SIZE_BYTES,
      });

      if (!validationResult.valid) {
        showErrorAlert(validationResult.message);

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        return;
      }

      try {
        for (const file of files) {
          uploadFile?.({ file })
            .then((url) => {
              // @ts-expect-error Wrong tiptap types
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              editor?.chain().focus().setImage({ src: url, loading: 'lazy' }).run();
            })
            .catch((e) => {
              console.error(e);
            });
        }
      } catch (e) {
        console.error(e);
        showErrorAlert('Something went wrong attaching the files, please try again later');
      } finally {
        /**
         * next change event might not be triggered because the file input value hasn't
         * technically changed if selecting the same file again.
         * To ensure the change event is always triggered, so resetting the value
         * */
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [editor, fileInputRef, showErrorAlert, uploadFile],
  );

  return (
    <Stack direction="row" sx={{ padding: '6px 0px', gap: '2px' }} justifyContent="end">
      <EditorToolbarButtons formattingButtons={toolbarButtons} />
      <FilePickerNode
        fileInputRef={fileInputRef}
        currentFiles={files}
        setCurrentFiles={setFiles}
        accept="image/*,"
        onChange={handleFileChange}
      />
    </Stack>
  );
};
